import type { HawkPluginModule, PluginManifest } from './types';

type GetValidToken = () => Promise<string | null>;
export type PluginFetch = (path: string, init?: RequestInit) => Promise<Response>;

/**
 * Build an authenticated fetch bound to a plugin's base URL. A fresh token is
 * attached on every call, so plugin requests inherit silent refresh. This is
 * the only credential a browser plugin ever sees.
 */
export function createPluginFetch(
  baseUrl: string,
  getValidToken: GetValidToken
): PluginFetch {
  const base = baseUrl.replace(/\/$/, '');
  return async function pluginFetch(path, init) {
    const url = /^https?:\/\//.test(path)
      ? path
      : `${base}${path.startsWith('/') ? path : `/${path}`}`;
    const token = await getValidToken();
    const headers = new Headers(init?.headers);
    if (token) headers.set('Authorization', `Bearer ${token}`);
    return fetch(url, { ...init, headers });
  };
}

async function fetchManifest(pluginFetch: PluginFetch): Promise<PluginManifest> {
  const response = await pluginFetch('/manifest.json');
  if (!response.ok) {
    throw new Error(
      `Failed to load plugin manifest (${response.status} ${response.statusText})`
    );
  }
  return (await response.json()) as PluginManifest;
}

/**
 * Fetch a browser plugin's entry bundle (with the Bearer header) and execute
 * it from a blob URL. The bundle must be a single self-contained ES module:
 * the browser issues no header on `<script src>` or lazy `import()`, so
 * anything the module loaded at runtime would be unauthenticated.
 */
export async function loadPluginModule(
  pluginFetch: PluginFetch
): Promise<HawkPluginModule> {
  const manifest = await fetchManifest(pluginFetch);
  const entry = manifest.entry ?? 'entry.js';
  const response = await pluginFetch(entry.startsWith('/') ? entry : `/${entry}`);
  if (!response.ok) {
    throw new Error(
      `Failed to load plugin entry (${response.status} ${response.statusText})`
    );
  }
  const source = await response.text();
  const objectUrl = URL.createObjectURL(
    new Blob([source], { type: 'text/javascript' })
  );
  try {
    const module = (await import(/* @vite-ignore */ objectUrl)) as unknown;
    if (!module || typeof (module as HawkPluginModule).mount !== 'function') {
      throw new Error('Plugin entry does not export a `mount` function');
    }
    return module as HawkPluginModule;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}
